"use client"

import Image from "next/image"
import Link from "next/link"
import { useTranslations } from "next-intl"
import { ArrowLeft, MapPin, Pencil, Plus } from "lucide-react"
import { formatCity, formatState } from "@/lib/format-location"
import { JobInquireButton } from "@/components/hiring/job-inquire-button"
import { AnnouncementLink } from "@/components/hiring/announcement-link"
import { PulseDot } from "@/components/ui/pulse-dot"
import {
  ACTIVE_LABEL, CHIP, CHIP_TONE, EDIT_BUTTON, EYEBROW, SECONDARY_LINK, SECTION,
} from "@/components/hiring/role-ui"
import type { JobRow } from "@/components/hiring/hiring-browse-shell"

// The role detail, inline. Same design AND same message keys as
// app/[locale]/hiring/[slug]/page.tsx — active pill, shop row, title, meta
// chips, compensation line, About this role, specialties, clientele, then
// the View-shop/Inquire action row — just living under the Job Board map
// instead of on its own page, so opening a role never unloads the map or
// the visitor's filter state.
//
// Renders entirely from the row the board already fetched: opening a role is
// a state change, not a request.
//
// Own-post handling mirrors the detail page: the owner of THIS listing gets
// the "This is your posting" pill and an Edit shortcut instead of Inquire
// (JobInquireButton would disable itself anyway — belt and braces).

export function JobRolePanel({
  job,
  previewMode = false,
  currentUserId = null,
  onBack,
}: {
  job: JobRow
  /** Owner sneak-peeking the public board (?preview=1) — Inquire suppressed. */
  previewMode?: boolean
  /** Viewer's user id, threaded from the server page — drives isOwnPost. */
  currentUserId?: string | null
  onBack: () => void
}) {
  const t = useTranslations("pages.hiring")
  // DB enum values become translated labels; unknown values fall back to the
  // raw value — identical to the detail page's helper.
  const label = (group: string, key: string) =>
    t.has(`labels.${group}.${key}`) ? t(`labels.${group}.${key}`) : key

  const shop = job.shop
  if (!shop) return null

  const isOwnPost = !!currentUserId && currentUserId === job.owner_id
  const cityLabel = [formatCity(shop.city || ""), formatState(shop.state || "")].filter(Boolean).join(", ")
  const shopHref = `/listings/${shop.slug || shop.id}`
  const editHref = `/dashboard/owner/shop/${shop.id}/hiring/${job.id}/edit`

  const backInline = (e: React.MouseEvent) => {
    e.preventDefault()
    onBack()
  }

  return (
    <div className="max-w-3xl animate-in fade-in slide-in-from-top-2 duration-300">
      {/* Top context strip — same two states as the detail page, except
          "back" restores the list in place instead of navigating. Real
          /hiring hrefs keep middle-click/new-tab working. */}
      {isOwnPost ? (
        <Link
          href="/hiring"
          onClick={backInline}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium bg-foreground text-background hover:opacity-90 transition-opacity mb-5"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.75} />
          <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} />
          {t("detail.ownPostBanner")}
        </Link>
      ) : (
        <div className="flex justify-start mb-5">
          <Link
            href="/hiring"
            onClick={backInline}
            className="inline-flex items-center gap-1.5 text-[13px] font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {t("detail.allOpenPositions")}
          </Link>
        </div>
      )}

      {/* Active marker — the board's own live indicator (PulseDot: the brand
          green lozenge with its ripple and breathing core) plus a plain label,
          instead of the one-off filled pill this panel drew for itself. Same
          marker the job rows and cards carry, so "live" looks the same
          everywhere. */}
      <div className="flex items-center gap-2 mb-3">
        <PulseDot />
        <span className={ACTIVE_LABEL}>
          {t("detail.active")}
        </span>
      </div>

      {/* Shop row */}
      <Link href={shopHref} className="flex items-center gap-3 mb-5 hover:opacity-80 transition-opacity">
        <div className="relative w-9 h-9 rounded-full overflow-hidden bg-muted border border-gray-200 flex-shrink-0">
          {shop.logo_url && (
            <Image src={shop.logo_url} alt={shop.name} fill className="object-cover" sizes="36px" />
          )}
        </div>
        <div className="min-w-0">
          <p className="font-semibold text-sm truncate">{shop.name}</p>
          {cityLabel && (
            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{cityLabel}</span>
            </p>
          )}
        </div>
      </Link>

      {/* Title */}
      <h2 className="text-2xl sm:text-[28px] font-bold leading-[1.15] tracking-tight mb-3">{job.title}</h2>

      {/* Meta strip */}
      <div className="flex flex-wrap gap-1.5 mb-5">
        {(job.position_type || []).map((p) => (
          <span key={`pt-${p}`} className={`${CHIP} ${CHIP_TONE.amber}`}>
            {label("position", p)}
          </span>
        ))}
        {job.compensation && (
          <span className={`${CHIP} ${CHIP_TONE.sky}`}>
            {label("compensation", job.compensation)}
          </span>
        )}
        {job.experience_required && (
          <span className={`${CHIP} ${CHIP_TONE.neutral}`}>
            {label("experience", job.experience_required)}
          </span>
        )}
      </div>

      {/* Compensation — the number a barber scans for, so it gets its own
          line between the chips and the CTA instead of trailing under them. */}
      {job.compensation_details && (
        <div className="mb-5">
          <p className={EYEBROW}>{t("detail.rate")}</p>
          <p className="text-lg font-semibold tracking-tight text-emerald-600 dark:text-emerald-400 tabular-nums">
            {job.compensation_details}
          </p>
        </div>
      )}

      {/* Primary action row. This used to be two rows — a mobile one here and
          a desktop one past the clientele chips — so on a long posting the only
          clickable Inquire sat below the fold of the panel's scroll. One row,
          at every breakpoint, directly under the role summary: the CTA is on
          screen the moment a role opens. */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-3 mb-7">
        {isOwnPost ? (
          <Link
            href={editHref}
            className={EDIT_BUTTON}
            aria-label={t("detail.editPosting")}
          >
            <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} />
            {t("detail.editPosting")}
          </Link>
        ) : !previewMode && (
          <JobInquireButton
            listingId={job.id}
            listingTitle={job.title}
            shopId={shop.id}
            shopName={shop.name}
            ownerId={job.owner_id}
            variant="pill"
          />
        )}
        <Link
          href={shopHref}
          className={SECONDARY_LINK}
          aria-label={t("detail.viewShop", { shop: shop.name })}
        >
          {t("detail.viewShop", { shop: shop.name })}
          <Plus className="w-3.5 h-3.5" strokeWidth={1.75} />
        </Link>
      </div>

      {/* Description */}
      {job.description && (
        <div className={SECTION}>
          <h3 className={EYEBROW}>{t("detail.aboutRole")}</h3>
          <p className="text-[15px] leading-relaxed text-foreground/90 whitespace-pre-line">{job.description}</p>
        </div>
      )}

      {/* Original announcement — the IG post / flyer / site the owner also
          announced this on. Brand mark + "Also on …", new tab. */}
      {job.original_announcement_url && (
        <div className={SECTION}>
          <AnnouncementLink url={job.original_announcement_url} />
        </div>
      )}

      {/* Specialties */}
      {(job.specialties || []).length > 0 && (
        <div className={SECTION}>
          <h3 className={EYEBROW}>{t("detail.specialties")}</h3>
          <div className="flex flex-wrap gap-1.5">
            {job.specialties.map((s) => (
              <span key={`sp-${s}`} className={`${CHIP} ${CHIP_TONE.violet}`}>
                {label("specialty", s)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Clientele preference */}
      {(job.clientele_pref || []).length > 0 && (
        <div className={SECTION}>
          <h3 className={EYEBROW}>{t("detail.preferredClientele")}</h3>
          <div className="flex flex-wrap gap-1.5">
            {job.clientele_pref!.map((c) => (
              <span key={`cl-${c}`} className={`${CHIP} ${CHIP_TONE.green}`}>
                {label("clientele", c)}
              </span>
            ))}
          </div>
        </div>
      )}

    </div>
  )
}
